export interface TemplateField {
  key: string;
  label: string;
  type: 'text' | 'email' | 'tel' | 'date' | 'textarea' | 'number';
  placeholder?: string;
  required?: boolean;
  defaultValue?: string;
  rows?: number;
  fullWidth?: boolean;
}

export interface FieldSection {
  title: string;
  icon: string;
  fields: TemplateField[];
  cols?: 1 | 2;
}

export const TEMPLATE_FIELDS: Record<string, FieldSection[]> = {
  '1': [
    {
      title: 'Your Business',
      icon: 'ri-building-line',
      fields: [
        { key: 'YOUR_COMPANY', label: 'Company Name', type: 'text', placeholder: 'Acme Corp, LLC', required: true },
        { key: 'YOUR_ADDRESS', label: 'Business Address', type: 'text', placeholder: '123 Main St, New York, NY 10001', required: true },
        { key: 'YOUR_EMAIL', label: 'Billing Email', type: 'email', placeholder: 'billing@yourcompany.com', required: true },
        { key: 'YOUR_PHONE', label: 'Phone Number', type: 'tel', placeholder: '(212) 555-0100', required: true },
      ],
    },
    {
      title: 'Invoice Details',
      icon: 'ri-file-text-line',
      cols: 2,
      fields: [
        { key: 'INVOICE_NUMBER', label: 'Invoice Number', type: 'text', placeholder: 'INV-2026-001', required: true },
        { key: 'TAX_RATE', label: 'Tax Rate (%)', type: 'number', placeholder: '0' },
        { key: 'INVOICE_DATE', label: 'Invoice Date', type: 'date', required: true },
        { key: 'DUE_DATE', label: 'Due Date', type: 'date', required: true },
      ],
    },
    {
      title: 'Bill To (Client)',
      icon: 'ri-user-line',
      fields: [
        { key: 'CLIENT_NAME', label: 'Client / Company Name', type: 'text', placeholder: 'Client Company Inc.', required: true },
        { key: 'CLIENT_ADDRESS', label: 'Client Address', type: 'text', placeholder: '456 Client St, San Francisco, CA 94102' },
        { key: 'CLIENT_EMAIL', label: 'Client Email', type: 'email', placeholder: 'accounts@client.com' },
      ],
    },
    {
      title: 'Services Rendered',
      icon: 'ri-list-check-2',
      cols: 2,
      fields: [
        { key: 'ITEM1_DESC', label: 'Item 1 — Description', type: 'text', placeholder: 'Brand Strategy Consulting', required: true, fullWidth: true },
        { key: 'ITEM1_QTY', label: 'Qty / Hours', type: 'text', placeholder: '10 hrs', required: true },
        { key: 'ITEM1_PRICE', label: 'Unit Price ($)', type: 'number', placeholder: '150', required: true },

        { key: 'ITEM2_DESC', label: 'Item 2 — Description', type: 'text', placeholder: 'Website Design & Development', fullWidth: true },
        { key: 'ITEM2_QTY', label: 'Qty', type: 'text', placeholder: '1' },
        { key: 'ITEM2_PRICE', label: 'Unit Price ($)', type: 'number', placeholder: '2500' },

        { key: 'ITEM3_DESC', label: 'Item 3 — Description (optional)', type: 'text', placeholder: 'Content Creation Package', fullWidth: true },
        { key: 'ITEM3_QTY', label: 'Qty', type: 'text', placeholder: '5' },
        { key: 'ITEM3_PRICE', label: 'Unit Price ($)', type: 'number', placeholder: '200' },

        { key: 'ITEM4_DESC', label: 'Item 4 — Description (optional)', type: 'text', placeholder: 'Monthly Maintenance', fullWidth: true },
        { key: 'ITEM4_QTY', label: 'Qty', type: 'text', placeholder: '1' },
        { key: 'ITEM4_PRICE', label: 'Unit Price ($)', type: 'number', placeholder: '350' },
      ],
    },
    {
      title: 'Payment Info',
      icon: 'ri-bank-line',
      fields: [
        { key: 'PAYMENT_INFO', label: 'Payment Instructions', type: 'textarea', placeholder: 'Bank: Chase | Account: XXXX | Routing: XXXX', rows: 3 },
      ],
    },
  ],

  '2': [
    {
      title: 'Service Provider',
      icon: 'ri-briefcase-line',
      fields: [
        { key: 'PROVIDER_NAME', label: 'Provider Name / Company', type: 'text', placeholder: 'Acme Corp, LLC', required: true },
        { key: 'PROVIDER_ADDRESS', label: 'Provider Address', type: 'text', placeholder: '123 Business Ave, New York, NY 10001', required: true },
        { key: 'PROVIDER_EMAIL', label: 'Provider Email', type: 'email', placeholder: 'contact@yourcompany.com', required: true },
      ],
    },
    {
      title: 'Client Information',
      icon: 'ri-user-line',
      fields: [
        { key: 'CLIENT_NAME', label: 'Client Name / Company', type: 'text', placeholder: 'Client Company Inc.', required: true },
        { key: 'CLIENT_ADDRESS', label: 'Client Address', type: 'text', placeholder: '456 Client St, City, State ZIP' },
      ],
    },
    {
      title: 'Contract Terms',
      icon: 'ri-file-text-line',
      fields: [
        { key: 'CONTRACT_NUMBER', label: 'Contract Number', type: 'text', placeholder: 'SVC-2026-001', required: true },
        { key: 'CONTRACT_DATE', label: 'Contract Date', type: 'date', required: true },
        { key: 'SERVICES_DESC', label: 'Services Description', type: 'textarea', placeholder: 'Digital marketing, content creation, monthly reporting...', required: true, rows: 3 },
        { key: 'MONTHLY_RATE', label: 'Monthly Retainer Rate', type: 'text', placeholder: '$2,500.00', required: true },
        { key: 'CONTRACT_TERM', label: 'Contract Term', type: 'text', placeholder: '12 months', required: true },
        { key: 'HOURLY_RATE', label: 'Hourly Rate (for extra work)', type: 'text', placeholder: '$150/hr' },
      ],
    },
  ],

  '3': [
    {
      title: 'Personal Info',
      icon: 'ri-user-line',
      fields: [
        { key: 'FULL_NAME', label: 'Full Name', type: 'text', placeholder: 'Alexandra Chen', required: true },
        { key: 'JOB_TITLE', label: 'Professional Title', type: 'text', placeholder: 'Senior Product Manager', required: true },
        { key: 'EMAIL', label: 'Email Address', type: 'email', placeholder: 'your@email.com', required: true },
        { key: 'PHONE', label: 'Phone Number', type: 'tel', placeholder: '(415) 555-0182', required: true },
        { key: 'LOCATION', label: 'Location (City, State)', type: 'text', placeholder: 'San Francisco, CA', required: true },
        { key: 'LINKEDIN', label: 'LinkedIn URL', type: 'text', placeholder: 'linkedin.com/in/yourname' },
        { key: 'GITHUB', label: 'GitHub / Portfolio (optional)', type: 'text', placeholder: 'github.com/yourname' },
      ],
    },
    {
      title: 'Most Recent Job',
      icon: 'ri-briefcase-line',
      fields: [
        { key: 'JOB1_TITLE', label: 'Job Title', type: 'text', placeholder: 'Senior Software Engineer', required: true },
        { key: 'JOB1_COMPANY', label: 'Company Name', type: 'text', placeholder: 'Tech Company Inc.', required: true },
        { key: 'JOB1_DATES', label: 'Dates', type: 'text', placeholder: 'Jan 2024 – Present', required: true },
        { key: 'JOB1_BULLETS', label: 'Key Accomplishments (one per line)', type: 'textarea', placeholder: 'Led team of 10 engineers to deliver major product\nIncreased performance by 40%', rows: 4, required: true },
      ],
    },
    {
      title: 'Education & Skills',
      icon: 'ri-graduation-cap-line',
      fields: [
        { key: 'EDUCATION', label: 'Degree & University', type: 'text', placeholder: 'B.S. Computer Science — Stanford University', required: true },
        { key: 'EDUCATION_DATES', label: 'Education Dates', type: 'text', placeholder: '2018 – 2022' },
        { key: 'SKILLS', label: 'Skills (comma separated)', type: 'textarea', placeholder: 'React, TypeScript, Node.js, Python, Agile, Figma', rows: 2, required: true },
      ],
    },
  ],

  '4': [
    {
      title: 'Parties Involved',
      icon: 'ri-shield-line',
      fields: [
        { key: 'DISCLOSING_PARTY', label: 'Disclosing Party (Company Name)', type: 'text', placeholder: 'TechVision Inc.', required: true },
        { key: 'RECEIVING_PARTY', label: 'Receiving Party (Name/Company)', type: 'text', placeholder: 'John Smith / Contractor LLC', required: true },
        { key: 'AGREEMENT_DATE', label: 'Agreement Date', type: 'date', required: true },
      ],
    },
    {
      title: 'NDA Terms',
      icon: 'ri-file-text-line',
      fields: [
        { key: 'NDA_TERM', label: 'Duration of Agreement', type: 'text', placeholder: '3 years', required: true },
        { key: 'SURVIVAL_TERM', label: 'Survival Period After Termination', type: 'text', placeholder: '2 years' },
        { key: 'GOVERNING_STATE', label: 'Governing State / Jurisdiction', type: 'text', placeholder: 'New York', required: true },
        { key: 'PURPOSE', label: 'Purpose of Disclosure', type: 'textarea', placeholder: 'Evaluating a potential business partnership and software development agreement', rows: 2 },
      ],
    },
  ],

  '5': [
    {
      title: 'Company Info',
      icon: 'ri-building-line',
      fields: [
        { key: 'COMPANY_NAME', label: 'Company Name', type: 'text', placeholder: 'Acme Corp, LLC', required: true },
        { key: 'COMPANY_ADDRESS', label: 'Company Address', type: 'text', placeholder: '123 Business Ave, New York, NY 10001', required: true },
        { key: 'HR_EMAIL', label: 'HR Email', type: 'email', placeholder: 'hr@company.com', required: true },
      ],
    },
    {
      title: 'Candidate Details',
      icon: 'ri-user-line',
      fields: [
        { key: 'CANDIDATE_NAME', label: 'Candidate Full Name', type: 'text', placeholder: 'Jordan Smith', required: true },
        { key: 'POSITION_TITLE', label: 'Job Title / Position', type: 'text', placeholder: 'Senior Software Engineer', required: true },
        { key: 'DEPARTMENT', label: 'Department', type: 'text', placeholder: 'Engineering', required: true },
        { key: 'MANAGER_NAME', label: 'Reporting Manager / Title', type: 'text', placeholder: 'Director of Engineering', required: true },
        { key: 'WORK_LOCATION', label: 'Work Location / Arrangement', type: 'text', placeholder: 'New York, NY (Hybrid — 3 days in office)', required: true },
      ],
    },
    {
      title: 'Compensation',
      icon: 'ri-money-dollar-circle-line',
      fields: [
        { key: 'START_DATE', label: 'Start Date', type: 'date', required: true },
        { key: 'BASE_SALARY', label: 'Base Salary (Annual)', type: 'text', placeholder: '$135,000', required: true },
        { key: 'SIGNING_BONUS', label: 'Signing Bonus (optional)', type: 'text', placeholder: '$5,000' },
        { key: 'BONUS_PERCENT', label: 'Annual Bonus %', type: 'text', placeholder: '15%' },
        { key: 'OFFER_EXPIRY', label: 'Offer Expiry Date', type: 'date', required: true },
      ],
    },
  ],

  '6': [
    {
      title: 'Project Details',
      icon: 'ri-task-line',
      fields: [
        { key: 'PROJECT_NAME', label: 'Project Name', type: 'text', placeholder: 'Website Redesign Launch', required: true },
        { key: 'PROJECT_MANAGER', label: 'Project Manager', type: 'text', placeholder: 'Sarah K.', required: true },
        { key: 'TARGET_DATE', label: 'Target Launch', type: 'text', placeholder: 'Q4 2026', required: true },
        { key: 'PROJECT_DATE', label: 'Date Created', type: 'date', required: true },
      ],
    },
  ],

  '7': [
    {
      title: 'Your Company',
      icon: 'ri-building-line',
      fields: [
        { key: 'YOUR_COMPANY', label: 'Your Company Name', type: 'text', placeholder: 'Acme Corp, LLC', required: true },
        { key: 'YOUR_ADDRESS', label: 'Company Address', type: 'text', placeholder: '123 Business Ave, New York, NY', required: true },
      ],
    },
    {
      title: 'Proposal Details',
      icon: 'ri-file-text-line',
      fields: [
        { key: 'CLIENT_NAME', label: 'Client / Company Name', type: 'text', placeholder: 'Client Company Inc.', required: true },
        { key: 'PROPOSAL_DATE', label: 'Proposal Date', type: 'date', required: true },
        { key: 'TOTAL_INVESTMENT', label: 'Total Investment', type: 'text', placeholder: '$48,000', required: true },
        { key: 'DEPOSIT_AMOUNT', label: 'Deposit Amount (25%)', type: 'text', placeholder: '$12,000', required: true },
        { key: 'PROBLEM_STATEMENT', label: 'Problem Statement', type: 'textarea', placeholder: 'Describe the client\'s current challenges...', rows: 3, required: true },
        { key: 'PROPOSED_SOLUTION', label: 'Proposed Solution Summary', type: 'textarea', placeholder: 'Phase 1: ... Phase 2: ... Phase 3: ...', rows: 3, required: true },
      ],
    },
  ],

  '8': [
    {
      title: 'Parties',
      icon: 'ri-user-line',
      fields: [
        { key: 'FREELANCER_NAME', label: 'Freelancer Name', type: 'text', placeholder: 'Jane Designer', required: true },
        { key: 'CLIENT_NAME', label: 'Client Name / Company', type: 'text', placeholder: 'Client Company Inc.', required: true },
        { key: 'CONTRACT_DATE', label: 'Contract Date', type: 'date', required: true },
        { key: 'CONTRACT_NUMBER', label: 'Contract Number', type: 'text', placeholder: 'FWA-2026-001', required: true },
      ],
    },
    {
      title: 'Project & Payment',
      icon: 'ri-money-dollar-circle-line',
      fields: [
        { key: 'PROJECT_TOTAL', label: 'Total Project Fee', type: 'text', placeholder: '$4,800', required: true },
        { key: 'DEPOSIT_AMOUNT', label: 'Upfront Deposit (50%)', type: 'text', placeholder: '$2,400', required: true },
        { key: 'HOURLY_RATE', label: 'Hourly Rate (extra work)', type: 'text', placeholder: '$85/hr', required: true },
        { key: 'DELIVERABLES', label: 'Deliverables (one per line)', type: 'textarea', placeholder: 'Brand Identity Package\nMarketing Collateral\nSocial Media Kit', rows: 3, required: true },
      ],
    },
  ],

  '9': [
    {
      title: 'Employee Info',
      icon: 'ri-user-line',
      fields: [
        { key: 'EMPLOYEE_NAME', label: 'Employee Name', type: 'text', placeholder: 'Michael Torres', required: true },
        { key: 'DEPARTMENT', label: 'Department', type: 'text', placeholder: 'Sales', required: true },
        { key: 'EMPLOYEE_ID', label: 'Employee ID', type: 'text', placeholder: 'EMP-0412', required: true },
        { key: 'MANAGER_NAME', label: 'Manager Name', type: 'text', placeholder: 'Rachel Kim', required: true },
        { key: 'REPORT_PERIOD', label: 'Report Period', type: 'text', placeholder: 'March 1 – March 31, 2026', required: true },
      ],
    },
    {
      title: 'Expense Purpose',
      icon: 'ri-flight-takeoff-line',
      fields: [
        { key: 'EXPENSE_PURPOSE', label: 'Purpose / Business Justification', type: 'textarea', placeholder: 'Q1 client visit and onboarding meetings in Chicago...', rows: 3, required: true },
      ],
    },
  ],

  '10': [
    {
      title: 'Employee Details',
      icon: 'ri-user-line',
      fields: [
        { key: 'EMPLOYEE_NAME', label: 'Employee Name', type: 'text', placeholder: 'David Okonkwo', required: true },
        { key: 'POSITION', label: 'Position / Title', type: 'text', placeholder: 'Account Executive', required: true },
        { key: 'DEPARTMENT', label: 'Department', type: 'text', placeholder: 'Sales', required: true },
        { key: 'MANAGER_NAME', label: 'Manager Name', type: 'text', placeholder: 'Sarah Mitchell', required: true },
        { key: 'REVIEW_YEAR', label: 'Review Year', type: 'text', placeholder: '2026', required: true },
        { key: 'REVIEW_DATE', label: 'Review Date', type: 'date', required: true },
      ],
    },
    {
      title: 'Review Content',
      icon: 'ri-star-line',
      fields: [
        { key: 'GOALS_ACHIEVED', label: 'Goals & Achievements', type: 'textarea', placeholder: 'Goal 1: Hit $1.2M quota — Achieved 115%\nGoal 2: Expand into 3 new verticals — Achieved', rows: 3 },
        { key: 'MANAGER_COMMENTS', label: 'Manager Comments', type: 'textarea', placeholder: 'Employee has shown exceptional skills...', rows: 3 },
        { key: 'DEVELOPMENT_PLAN', label: 'Development Plan (Next 12 Months)', type: 'textarea', placeholder: '1. Complete advanced certification by Q1\n2. Mentor junior team member', rows: 3 },
      ],
    },
  ],

  '11': [
    {
      title: 'Your Business',
      icon: 'ri-building-line',
      fields: [
        { key: 'YOUR_COMPANY', label: 'Company Name', type: 'text', placeholder: 'Your Company Name', required: true },
        { key: 'YOUR_EMAIL', label: 'Business Email', type: 'email', placeholder: 'billing@company.com', required: true },
        { key: 'YOUR_PHONE', label: 'Phone Number', type: 'tel', placeholder: '(555) 000-0000', required: true },
        { key: 'YOUR_ADDRESS', label: 'Business Address', type: 'text', placeholder: '123 Main St, City, State ZIP' },
      ],
    },
  ],

  '12': [
    {
      title: 'Meeting Info',
      icon: 'ri-calendar-line',
      fields: [
        { key: 'MEETING_TITLE', label: 'Meeting Title', type: 'text', placeholder: 'Q3 Quarterly Business Review', required: true },
        { key: 'MEETING_DATE', label: 'Meeting Date', type: 'date', required: true },
        { key: 'MEETING_TIME', label: 'Time (e.g. 10:00 AM – 12:00 PM EST)', type: 'text', placeholder: '10:00 AM – 12:00 PM EST', required: true },
        { key: 'MEETING_LOCATION', label: 'Location / Video Link', type: 'text', placeholder: 'Conference Room A / Zoom', required: true },
        { key: 'FACILITATOR', label: 'Facilitator Name', type: 'text', placeholder: 'Sarah Kim', required: true },
      ],
    },
    {
      title: 'Attendees & Objectives',
      icon: 'ri-group-line',
      fields: [
        { key: 'ATTENDEES', label: 'Attendees (comma or line separated)', type: 'textarea', placeholder: 'Sarah Kim (CEO), James Lee (CFO), Maria Ortiz (VP Sales)', rows: 3, required: true },
        { key: 'MEETING_OBJECTIVES', label: 'Meeting Objectives', type: 'textarea', placeholder: '1. Review Q3 performance\n2. Align on Q4 priorities\n3. Identify blockers', rows: 3 },
      ],
    },
  ],
};
